import {
  BuildingLibraryIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  CurrencyEuroIcon,
  EnvelopeOpenIcon,
  UserCircleIcon,
  WalletIcon,
} from "@heroicons/react/24/solid";

import type { ClaimWithRelations } from "../api/use-claim";
import { type ClaimLegalStepName, claimLegalStepById, legalSteps } from "../lib/legal-steps";

type Status = "complete" | "current" | "upcoming";

interface Step {
  name: string;
  stepName: ClaimLegalStepName;
  Icon: typeof CheckIcon;
  completeClass: string;
  currentBorderClass: string;
  currentTextClass: string;
}

const stepsCatalog: Record<string, Step> = {
  CLAIM_VALIDATION: {
    name: "Validation",
    stepName: "CLAIM_VALIDATION",
    Icon: UserCircleIcon,
    completeClass: "bg-teal-700/90 group-hover:bg-teal-700",
    currentBorderClass: "border-teal-700",
    currentTextClass: "text-teal-700",
  },
  UPCLAIM: {
    name: "Amiable",
    stepName: "UPCLAIM",
    Icon: ChatBubbleLeftRightIcon,
    completeClass: "bg-blue-700/90 group-hover:bg-blue-700",
    currentBorderClass: "border-blue-700",
    currentTextClass: "text-blue-700",
  },
  CONCILIATION: {
    name: "Précontentieux",
    stepName: "CONCILIATION",
    Icon: EnvelopeOpenIcon,
    completeClass: "bg-amber-600/90 group-hover:bg-amber-600",
    currentBorderClass: "border-amber-600",
    currentTextClass: "text-amber-600",
  },
  COURT: {
    name: "Contentieux",
    stepName: "COURT",
    Icon: BuildingLibraryIcon,
    completeClass: "bg-red-700/90 group-hover:bg-red-700",
    currentBorderClass: "border-red-700",
    currentTextClass: "text-red-700",
  },
  WAITING_FOR_MONEY: {
    name: "Attente de paiement",
    stepName: "WAITING_FOR_MONEY",
    Icon: CurrencyEuroIcon,
    completeClass: "bg-violet-700/90 group-hover:bg-violet-700",
    currentBorderClass: "border-violet-700",
    currentTextClass: "text-violet-700",
  },
  MONEY_RECEIVED: {
    name: "Paiement reçu",
    stepName: "MONEY_RECEIVED",
    Icon: WalletIcon,
    completeClass: "bg-green-700/90 group-hover:bg-green-700",
    currentBorderClass: "border-green-700",
    currentTextClass: "text-green-700",
  },
};

/** Port of ClaimSteps.vue getSteps(). */
function getSteps(claim: ClaimWithRelations): Step[] {
  const claimLegalStepIds = claim.claimEvents.reduce((acc, claimEvent) => {
    if (!acc.includes(claimEvent.eventType.claimLegalStepId)) {
      acc.push(claimEvent.eventType.claimLegalStepId);
    }
    return acc;
  }, [] as number[]);
  const claimLegalStepNames = claimLegalStepIds.map((id) => claimLegalStepById(id));

  const currentClaimStep = claim.claimLegalStep.name;
  if (currentClaimStep === "CLAIM_VALIDATION") {
    return [stepsCatalog.CLAIM_VALIDATION, stepsCatalog.UPCLAIM, stepsCatalog.CONCILIATION];
  }
  if (currentClaimStep === "UPCLAIM" || currentClaimStep === "CONCILIATION" || currentClaimStep === "COURT") {
    return [stepsCatalog.UPCLAIM, stepsCatalog.CONCILIATION, stepsCatalog.COURT];
  }
  if (currentClaimStep === "WAITING_FOR_MONEY") {
    const isCourt = claimLegalStepNames.includes("COURT");
    const isConciliation = claimLegalStepNames.includes("CONCILIATION");

    const claimSteps = [stepsCatalog.WAITING_FOR_MONEY, stepsCatalog.MONEY_RECEIVED];

    if (isCourt) {
      return [stepsCatalog.COURT, ...claimSteps];
    }
    if (isConciliation) {
      return [stepsCatalog.CONCILIATION, ...claimSteps];
    }
    return [stepsCatalog.UPCLAIM, ...claimSteps];
  }
  return [];
}

/** Port of ClaimSteps.vue — legal step progress bar. */
export function ClaimSteps({ claim }: { claim: ClaimWithRelations }) {
  const steps = getSteps(claim);

  function getStatus(stepName: ClaimLegalStepName): Status {
    const currentClaimStep = claim.claimLegalStep;
    const step = legalSteps[stepName];

    if (step.id < currentClaimStep.id) {
      return "complete";
    }
    if (step.id === currentClaimStep.id) {
      return "current";
    }
    return "upcoming";
  }

  return (
    <div className="sticky top-0 z-10 bg-white/75 backdrop-blur-xs sm:border-t sm:border-b sm:border-gray-200">
      <nav aria-label="Progress">
        <ol className="sm:flex sm:divide-y-0">
          {steps.map((step, stepIdx) => {
            const status = getStatus(step.stepName);
            const spacing = stepIdx === 1 ? "mr-5" : "";
            return (
              <li key={step.name} className="relative sm:flex sm:flex-1">
                {status === "complete" ? (
                  <span className={`group flex w-full cursor-default items-center ${spacing}`.trim()}>
                    <span className="flex items-center px-5 py-2 text-sm font-medium">
                      <span
                        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${step.completeClass}`}
                      >
                        <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" />
                      </span>
                      <span className="ml-3 text-sm font-medium text-gray-900/80">{step.name}</span>
                    </span>
                  </span>
                ) : status === "current" ? (
                  <span
                    aria-current="step"
                    className={`flex cursor-default items-center px-5 py-2 text-sm font-medium ${spacing}`.trim()}
                  >
                    <span
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 ${step.currentBorderClass}`}
                    >
                      <step.Icon className={`h-5 w-5 ${step.currentTextClass}`} aria-hidden="true" />
                    </span>
                    <span className={`ml-3 text-sm font-medium ${step.currentTextClass}`}>{step.name}</span>
                  </span>
                ) : (
                  <span className={`group flex items-center ${spacing}`.trim()}>
                    <span className="flex items-center px-5 py-2 text-sm font-medium">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-zinc-200 group-hover:border-zinc-300">
                        <step.Icon
                          className="h-5 w-5 text-zinc-300 group-hover:text-zinc-500"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="ml-3 text-sm font-medium text-gray-500 group-hover:text-gray-900">
                        {step.name}
                      </span>
                    </span>
                  </span>
                )}

                {stepIdx !== steps.length - 1 ? (
                  <div className="absolute top-0 right-0 hidden h-full w-5 sm:block" aria-hidden="true">
                    <svg
                      className="h-full w-full text-gray-300"
                      viewBox="0 0 22 80"
                      fill="none"
                      preserveAspectRatio="none"
                      role="presentation"
                    >
                      <path
                        d="M0 -2L20 40L0 82"
                        vectorEffect="non-scaling-stroke"
                        stroke="currentcolor"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
